import {
  isNativeAuthCode,
  type AuthProviderName,
  type AuthResultErrorReason,
  type ProviderCredentialJson,
} from '@sunflower-games/webview-bridge';
import { OAuthCredential } from 'firebase/auth';

import { getShellBridge } from './shellBridge';

export class NativeProviderSignInError extends Error {
  public reason: AuthResultErrorReason;

  constructor(reason: AuthResultErrorReason) {
    super(`Native provider sign-in failed: ${reason}`);
    this.reason = reason;
  }
}

/**
 * Five minutes. The bridge client's own default is 5000ms, which is the wrong timescale here: a person
 * is picking an account or typing a password into the provider's native sheet. Every `auth.signin`
 * is answered with `auth.result` by the shell, so this is a backstop for a shell that never answers,
 * not the mechanism.
 */
export const SIGN_IN_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * base64url (unpadded) UTF-8 decode — the inverse of `encodeProviderCredential` in
 * `@sunflower-games/webview-bridge`'s `native-auth.ts` — rehydrated into a Firebase credential. No
 * decoder ships in that package by design (per its doc comment): the receiving half belongs to the
 * web app, and this is that half for player-frontend, the same role `predictions-ui`'s
 * `decodeNativeCredential` plays there.
 *
 * Every step sits inside one guard, `fromJSON` included, and every failure is a fixed message: V8's
 * `SyntaxError` from `JSON.parse` quotes the start of its input, which here is credential material,
 * and the caller logs errors to New Relic.
 */
function decodeProviderCredential(code: string): OAuthCredential {
  let credential: OAuthCredential | null;

  try {
    const base64 = code.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const json = JSON.parse(new TextDecoder().decode(bytes)) as ProviderCredentialJson;

    credential = OAuthCredential.fromJSON(json);
  } catch {
    throw new Error('Native provider credential could not be decoded');
  }

  if (!credential) {
    throw new Error('Native provider credential could not be decoded into a Firebase credential');
  }

  return credential;
}

/**
 * Signs in with a provider credential the mobile shell produced natively (Google/Apple/Facebook's
 * own SDK, not a web OAuth redirect) — see `2026-08-17-native-provider-signin-design.md` in
 * `mobile-shell`. This is what makes social sign-in work inside the shell's WebView at all:
 * `signInWithRedirect` needs `sessionStorage` to survive a full navigation to the provider and
 * back, which WebView storage partitioning breaks (`auth/missing-initial-state`), and Google
 * additionally refuses to run its own OAuth page inside a detected WebView regardless.
 *
 * Callers must check `isInShell()` first — this throws if there is no bridge, rather than
 * silently resolving, so a caller can never mistake "not in a shell" for "user cancelled".
 *
 * Resolves `null` for a user-cancelled native sheet (mirrors `signInWithRedirect`'s
 * popup-closed-by-user case: no session, no error). Throws `NativeProviderSignInError` for a
 * provider the native side could not service or a native SDK failure.
 */
export async function signInWithNativeProvider(provider: AuthProviderName): Promise<OAuthCredential | null> {
  const bridge = getShellBridge();

  if (!bridge) {
    throw new Error('signInWithNativeProvider called without an active native bridge');
  }

  const result = await bridge.request('auth.signin', { provider }, 'auth.result', SIGN_IN_TIMEOUT_MS);

  switch (result.status) {
    case 'cancelled':
      return null;
    case 'error':
      throw new NativeProviderSignInError(result.reason);
    case 'success':
      if (!isNativeAuthCode(result.code)) {
        throw new Error('Native provider sign-in returned an empty credential code');
      }

      return decodeProviderCredential(result.code);
  }
}
